/**
 * Output formatting utilities for plain English responses
 */

import { logger } from "./logger.js";

/**
 * Render an arbitrary value for human-readable tool output.
 *
 * Tool results interpolate values that come back from APIs as `unknown` —
 * Dataverse column values, error payloads, response bodies. A bare template
 * interpolation renders a nested object as the literal string
 * "[object Object]", which is worse than useless in a table the user is
 * reading. Objects and arrays serialize to compact JSON instead; everything
 * else uses its natural string form.
 */
export function displayValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  if (value instanceof Date) return value.toISOString();
  if (value instanceof Error) return value.message;
  if (typeof value === "symbol" || typeof value === "function") return String(value);
  try {
    return JSON.stringify(value) ?? "";
  } catch {
    // Circular or otherwise unserializable — say so rather than "[object Object]".
    return "[unserializable value]";
  }
}

/**
 * Format a flow for display
 */
export function formatFlow(flow: {
  name: string;
  properties: {
    displayName: string;
    state?: string;
    createdTime?: string;
    lastModifiedTime?: string;
  };
}): string {
  const lines = [
    `**${flow.properties.displayName}**`,
    `ID: ${flow.name}`,
    `Status: ${flow.properties.state || "Unknown"}`,
  ];
  if (flow.properties.lastModifiedTime) {
    lines.push(`Modified: ${new Date(flow.properties.lastModifiedTime).toLocaleDateString()}`);
  }
  return lines.join("\n");
}

/**
 * Format a validation result
 */
export function formatValidation(result: {
  valid: boolean;
  schemaValidation?: { errors: Array<{ path: string; message: string }>; warnings: Array<{ path: string; message: string }> };
  expressionValidation?: { errors: Array<{ path: string; message: string; severity: string }> };
  connectionValidation?: { errors: Array<{ path: string; message: string; severity: string }> };
}): string {
  if (result.valid) {
    return "Flow validation passed.";
  }

  const lines = ["Flow validation failed:\n"];

  const schemaErrors = result.schemaValidation?.errors || [];
  const exprErrors = (result.expressionValidation?.errors || []).filter(e => e.severity === "error");
  const connErrors = (result.connectionValidation?.errors || []).filter(e => e.severity === "error");

  if (schemaErrors.length > 0) {
    lines.push("Schema issues:");
    schemaErrors.slice(0, 5).forEach(e => lines.push(`  • ${e.message}`));
    if (schemaErrors.length > 5) lines.push(`  ... and ${schemaErrors.length - 5} more`);
    lines.push("");
  }

  if (exprErrors.length > 0) {
    lines.push("Expression issues:");
    exprErrors.slice(0, 5).forEach(e => lines.push(`  • ${e.message}`));
    lines.push("");
  }

  if (connErrors.length > 0) {
    lines.push("Connection issues:");
    connErrors.slice(0, 5).forEach(e => lines.push(`  • ${e.message}`));
    lines.push("");
  }

  return lines.join("\n");
}

/**
 * How much of an ARM error message tool output preserves. ARM validation
 * errors put the actionable part (allowed enum values, property paths) at the
 * end of a long message, so aggressive truncation hides exactly the fix.
 */
export const ERROR_DETAIL_LIMIT = 2000;

// Escape-aware: the message value may contain \" sequences (it's often a
// JSON-stringified body). A bare [^"]+ stops at the first escaped quote and
// clips lists like '["user","meeting","adhocCallUser"]'.
const JSON_MESSAGE_PATTERN = /"message"\s*:\s*"((?:[^"\\]|\\.)*)"/;

function unescapeJsonString(raw: string): string {
  try {
    return JSON.parse(`"${raw}"`) as string;
  } catch {
    return raw;
  }
}

/**
 * Extract the deepest human-actionable message from an ARM-style error
 * payload. Accepts a parsed object ({error: {code, message}} or {message}),
 * a JSON string, or free text with an embedded JSON error body. ARM nests
 * error bodies inside message strings (sometimes multiple levels), so the
 * extraction recurses until it reaches plain text. Returns undefined when
 * no message field is present anywhere.
 */
export function extractArmErrorMessage(source: unknown, depth = 0): string | undefined {
  if (source == null || depth > 6) return undefined;

  if (typeof source === "string") {
    const text = source.trim();
    if (!text) return undefined;
    if (text.startsWith("{") || text.startsWith("[")) {
      try {
        const fromParsed = extractArmErrorMessage(JSON.parse(text), depth + 1);
        if (fromParsed) return fromParsed;
      } catch {
        // Truncated or otherwise malformed JSON — fall through to the regex.
      }
    }
    const match = text.match(JSON_MESSAGE_PATTERN);
    if (match?.[1]) {
      const message = unescapeJsonString(match[1]);
      return extractArmErrorMessage(message, depth + 1) ?? message;
    }
    return undefined;
  }

  if (typeof source !== "object") return undefined;

  const record = source as Record<string, unknown>;
  const nested = record.error ?? record.Error;
  if (nested && typeof nested === "object") {
    const fromNested = extractArmErrorMessage(nested, depth + 1);
    if (fromNested) return fromNested;
  }
  const message = record.message ?? record.Message;
  if (typeof message === "string" && message) {
    return extractArmErrorMessage(message, depth + 1) ?? message;
  }
  return undefined;
}

/**
 * Format an API error for user display
 */
export function formatApiError(error: unknown, context: string): string {
  let rawMessage: string;
  let details: string;

  if (error instanceof Error) {
    rawMessage = error.message;
  } else {
    rawMessage = String(error);
  }

  // Log full error for debugging
  logger.error({ error, context }, "API error occurred");

  // Extract useful info from common error patterns
  if (rawMessage.includes("ConnectionAuthorizationFailed")) {
    const connMatch = rawMessage.match(/apis\/([^/]+)/);
    const connector = connMatch ? connMatch[1]!.replace("shared_", "") : "a connector";
    return `Cannot ${context}: Missing or invalid connection for "${connector}".\n\nFix: Go to make.powerautomate.com > Data > Connections and create/fix this connection.`;
  }

  if (rawMessage.includes("FlowNotFound") || rawMessage.includes("ResourceNotFound")) {
    return `Cannot ${context}: Flow not found. It may have been deleted.`;
  }

  if (rawMessage.includes("InvalidTemplate") || rawMessage.includes("InvalidDefinition")) {
    // Try to extract specific error
    details = extractArmErrorMessage(rawMessage) ?? "Invalid flow definition";
    return `Cannot ${context}: ${details.slice(0, ERROR_DETAIL_LIMIT)}`;
  }

  if (rawMessage.includes("401") || rawMessage.includes("Unauthorized")) {
    return `Cannot ${context}: Authentication expired. Run /mcp to reconnect.`;
  }

  if (rawMessage.includes("403") || rawMessage.includes("Forbidden")) {
    return `Cannot ${context}: Access denied. Check your permissions.`;
  }

  if (rawMessage.includes("429") || rawMessage.includes("TooManyRequests")) {
    return `Cannot ${context}: Rate limited. Wait a moment and try again.`;
  }

  // Try to extract a clean message
  const codeMatch = rawMessage.match(/"code"\s*:\s*"([^"]+)"/);
  const extracted = extractArmErrorMessage(rawMessage);

  if (extracted) {
    details = extracted;
    if (codeMatch) {
      details = `${codeMatch[1]!}: ${details}`;
    }
    return `Cannot ${context}: ${details.slice(0, ERROR_DETAIL_LIMIT)}`;
  }

  // Fallback - truncate long messages
  const cleanMsg = rawMessage.replace(/\{[\s\S]*\}/g, "").trim() || rawMessage;
  return `Cannot ${context}: ${cleanMsg.slice(0, ERROR_DETAIL_LIMIT)}${cleanMsg.length > ERROR_DETAIL_LIMIT ? "..." : ""}`;
}

/**
 * Format a list of items
 */
export function formatList(
  title: string,
  items: Array<{ primary: string; secondary?: string; status?: "success" | "error" | "warning" }>,
  emptyMessage = "None found."
): string {
  if (items.length === 0) {
    return emptyMessage;
  }

  const lines = [`${title} (${items.length}):\n`];

  for (const item of items) {
    const icon = item.status === "error" ? "✗" : item.status === "warning" ? "!" : "•";
    lines.push(`${icon} ${item.primary}`);
    if (item.secondary) {
      lines.push(`  ${item.secondary}`);
    }
  }

  return lines.join("\n");
}
